import React from "react";
import { useParams, Link } from "react-router-dom";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { getArchetype } from "../data/archetypes";
import { personAssignments } from "../services/utilization";
import "../styles.css";

const TRAIT_NAMES = { A: "Autonomy", B: "Social Ability", C: "Pace", D: "Conformity" };
const dateOnly = (d) => (d ? String(d).slice(0, 10) : "");

function getTraitDescription(trait, val) {
  const d = {
    A: val >= 6 ? "High autonomy — assertive, visionary, comfortable leading and making decisions independently."
                : "Lower autonomy — cooperative, accommodating, prefers clear direction. Supports rather than leads.",
    B: val >= 6 ? "High social — energized by people, persuasive, processes thoughts by talking them through."
                : "Lower social — inward, analytical, prefers facts over feelings. Works well independently.",
    C: val >= 6 ? "High pace — methodical, patient, single-focus. Prefers routine, dislikes sudden change."
                : "Lower pace — fast-moving, multi-tasker, natural sense of urgency. Gets antsy with repetitive work.",
    D: val >= 6 ? "High conformity — rule-follower, detail-oriented, precise. Strong sense of duty and quality control."
                : "Lower conformity — conceptual thinker, delegates details, fearless and informal.",
  };
  return d[trait] || "";
}

function TraitCard({ trait, surveyVal, jobVal }) {
  const diff = Math.abs((jobVal ?? surveyVal) - surveyVal);
  const adapting = diff >= 3;
  return (
    <div className="trait-card">
      <div className="trait-card-head">
        <div className="trait-card-letter">
          <div className="trait-circle">{trait}</div>
          <span className="trait-name">{TRAIT_NAMES[trait]}</span>
        </div>
        <div className="trait-legend">
          <span className="tl-item"><span className="tl-dot survey" />Survey</span>
          <span className="tl-item"><span className="tl-dot job" />On Job</span>
        </div>
      </div>
      <div className="trait-barline">
        <div className="trait-track"><div className="trait-mid" /><div className="trait-fill survey" style={{ width: `${(surveyVal / 10) * 100}%` }} /></div>
        <span className="trait-bv survey">{surveyVal}</span>
      </div>
      <div className="trait-barline">
        <div className="trait-track"><div className="trait-mid" /><div className="trait-fill job" style={{ width: `${((jobVal ?? surveyVal) / 10) * 100}%` }} /></div>
        <span className="trait-bv job">{jobVal ?? surveyVal}</span>
      </div>
      {adapting && (
        <div className="trait-flag"><Icon name="alert" /> Adapting on the job — potential energy drain</div>
      )}
      <div className="trait-desc">{getTraitDescription(trait, surveyVal)}</div>
    </div>
  );
}

export default function PersonDetail() {
  const { id } = useParams();
  const { people, teamMembers, projects, loading, error } = useData();

  if (loading) {
    return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading profile…</div></div></div>;
  }
  if (error) {
    return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;
  }

  const person = people.find(p => String(p.Id) === String(id));

  if (!person) {
    return (
      <div className="page">
        <Link to="/people" className="back-btn"><Icon name="arrow-left" /> Back to People</Link>
        <div className="empty"><div className="empty-text">Person not found.</div></div>
      </div>
    );
  }

  const arch = getArchetype(person.Archetype);
  const util = personAssignments(person, teamMembers, projects);
  const current = util.rows;      // live assignments
  const past = util.pastRows;     // ended / rolled-off

  return (
    <div className="page">
      <Link to="/people" className="back-btn"><Icon name="arrow-left" /> Back to People</Link>

      {/* Hero */}
      <div className="profile-hero">
        <div className="profile-band" />
        <div className="profile-body">
          <div className="profile-badge">
            <div className="profile-badge-inner">
              <Icon name={arch.icon} />
              <span className="profile-badge-label">{person.Archetype}</span>
            </div>
          </div>
          <div className="profile-info">
            <div className="profile-name">{person.FullName}</div>
            <div className="profile-title">{[person.JobTitle, person.CompanyRole].filter(Boolean).join(" · ")}</div>
            <div className="profile-chips">
              <span className="profile-archetype-chip"><Icon name={arch.icon} /> {person.Archetype}</span>
              <span className="profile-group-chip">{arch.group} Group</span>
              <span className="profile-meta">
                <span>EU <b>{person.Survey_EU}</b></span>
                <span>L <b>{person.Survey_L}</b></span>
                <span>I <b>{person.Survey_I}</b></span>
              </span>
            </div>
          </div>
          <div className="profile-stat">
            <div className="profile-stat-num">{current.length}</div>
            <div className="profile-stat-label">Current<br />Assignments</div>
          </div>
        </div>
      </div>

      {/* Trait grid */}
      <div className="grid-2" style={{ marginBottom: "18px" }}>
        {["A", "B", "C", "D"].map(t => (
          <TraitCard key={t} trait={t} surveyVal={person[`Survey_${t}`]} jobVal={person[`Job_${t}`]} />
        ))}
      </div>

      {/* EU / L / I */}
      <div className="card mb-24">
        <div className="card-header"><div className="card-title">Energy · Logic · Ingenuity</div></div>
        <div className="grid-3">
          <div>
            <div style={{ fontSize: "20px", fontWeight: 800, color: "var(--green)", marginBottom: "4px" }}>EU {person.Survey_EU}</div>
            <div style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.6 }}>
              {person.Survey_EU <= 30 ? "Lower energy — needs recharge time through the day." :
               person.Survey_EU <= 60 ? "Average energy — sustains focus for extended periods without burnout risk." :
               "High energy — tireless, maintains consistent output."}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "20px", fontWeight: 800, color: "var(--blue)", marginBottom: "4px" }}>L {person.Survey_L}</div>
            <div style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.6 }}>
              {person.Survey_L <= 2 ? "High emotion — wears feelings openly." :
               person.Survey_L <= 7 ? "Balanced logic and emotion. Situationally adaptive." :
               "Pure logic — hard to read emotionally, cool under pressure."}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "20px", fontWeight: 800, color: "var(--gold-text)", marginBottom: "4px" }}>I {person.Survey_I}</div>
            <div style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.6 }}>
              {person.Survey_I <= 6 ? "Linear, literal thinker — reliable and straightforward." :
               "High ingenuity — abstract, multi-dimensional thinking. Generates unconventional ideas."}
            </div>
          </div>
        </div>
      </div>

      {/* Current Assignments */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Current Assignments</div>
          <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{current.length}</div>
        </div>
        {current.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "6px 0" }}>Not currently assigned — available on the bench.</div>
        ) : current.map((a, i) => (
          <Link key={i} to={`/projects/${encodeURIComponent(a.jobNumber)}`} style={{ textDecoration: "none", color: "inherit" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: i < current.length - 1 ? "1px solid var(--border)" : "none" }}>
              <div>
                <div style={{ fontWeight: 700 }}>
                  {a.jobName}
                  {a.status === "Potential" && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: "var(--gold-text)", textTransform: "uppercase" }}>Pipeline</span>}
                  {a.overrun && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "var(--red)" }}>⚠ past planned end</span>}
                </div>
                <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{a.project?.Address || ""}{a.end ? ` · ends ${dateOnly(a.end)}` : ""}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--gold-text)" }}>{a.jobNumber}</div>
                <div style={{ fontSize: "11px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "1px" }}>{a.role} · {a.alloc}%</div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Past / rolled-off Assignments */}
      {past.length > 0 && (
        <div className="card" style={{ marginTop: "18px" }}>
          <div className="card-header">
            <div className="card-title">Past Assignments</div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{past.length} · rolled off or completed</div>
          </div>
          {past.map((a, i) => (
            <Link key={i} to={`/projects/${encodeURIComponent(a.jobNumber)}`} style={{ textDecoration: "none", color: "inherit" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: i < past.length - 1 ? "1px solid var(--border)" : "none", opacity: 0.75 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{a.jobName}</div>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                    {a.member?.EndDate ? `Rolled off ${dateOnly(a.member.EndDate)}` : a.status} · {a.role}
                  </div>
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>{a.jobNumber}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
